'use client';

import React, {
  useEffect,
  useCallback,
  useReducer,
  useMemo,
  useRef,
} from 'react';
import type { NatsConnection } from 'nats.ws';
import {
  SocketManagerContext,
  type SocketManagerContextValue,
} from './context';
import type { SocketManagerProviderConfig } from '../types';
import PricesWebSocketManager from '../library/prices-manager';
import TradesWebSocketManager from '../library/trades-manager';
import USPricesWebSocketManager from '../library/us-prices-manager';
import NatsManager from '../library/nats-manager';
import { WebSocketClientType, WebsocketClientType } from '../constants/common';
import { PROVIDER_EVENT_NAMES } from '@constants/nr-events';

// ============================================
// State & Action Types
// ============================================

interface State {
  pricesConnection: WebSocket | null;
  tradesConnection: WebSocket | null;
  usPricesConnection: WebSocket | null;
  natsConnection: NatsConnection | null;
}

type Action =
  | { type: 'UPDATE_PRICES_CONNECTION'; payload: { ws: WebSocket | null } }
  | { type: 'UPDATE_TRADES_CONNECTION'; payload: { ws: WebSocket | null } }
  | { type: 'UPDATE_US_PRICES_CONNECTION'; payload: { ws: WebSocket | null } }
  | {
      type: 'UPDATE_NATS_CONNECTION';
      payload: { nats: NatsConnection | null };
    };

// ============================================
// Reducer
// ============================================

const initialState: State = {
  pricesConnection: null,
  tradesConnection: null,
  usPricesConnection: null,
  natsConnection: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'UPDATE_PRICES_CONNECTION':
      return { ...state, pricesConnection: action.payload.ws };
    case 'UPDATE_TRADES_CONNECTION':
      return { ...state, tradesConnection: action.payload.ws };
    case 'UPDATE_US_PRICES_CONNECTION':
      return { ...state, usPricesConnection: action.payload.ws };
    case 'UPDATE_NATS_CONNECTION':
      return { ...state, natsConnection: action.payload.nats };
    default:
      return state;
  }
}

// ============================================
// Provider Props
// ============================================

export interface SocketManagerProviderProps {
  config: SocketManagerProviderConfig;
  children: React.ReactNode;
  /** Callback when prices connection state changes (Indian stocks) */
  onPricesConnectionChange?: (ws: WebSocket | null) => void;
  /** Callback when trades connection state changes (Indian stocks) */
  onTradesConnectionChange?: (ws: WebSocket | null) => void;
  /** Callback when US stocks prices connection state changes */
  onUSPricesConnectionChange?: (ws: WebSocket | null) => void;
  /** Callback when NATS connection state changes */
  onNatsConnectionChange?: (nats: NatsConnection | null) => void;
  /** Whether to enable the provider (useful for conditional initialization) */
  enabled?: boolean;
}

// ============================================
// Provider Component
// ============================================

export function SocketManagerProvider({
  children,
  config,
  onPricesConnectionChange,
  onTradesConnectionChange,
  onUSPricesConnectionChange,
  onNatsConnectionChange,
  enabled = true,
}: SocketManagerProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { trackEvent = () => {} } = config?.analytics ?? {};

  // ============================================
  // Connection Callbacks
  // ============================================

  const setWebSocketConnection = useCallback(
    (ws: WebSocket | null, client: string) => {
      if (client === 'PRICES') {
        dispatch({ type: 'UPDATE_PRICES_CONNECTION', payload: { ws } });
        onPricesConnectionChange?.(ws);
      } else if (client === 'TRADES') {
        dispatch({ type: 'UPDATE_TRADES_CONNECTION', payload: { ws } });
        onTradesConnectionChange?.(ws);
      } else if (client === 'US_PRICES') {
        dispatch({ type: 'UPDATE_US_PRICES_CONNECTION', payload: { ws } });
        onUSPricesConnectionChange?.(ws);
      }
    },
    [
      onPricesConnectionChange,
      onTradesConnectionChange,
      onUSPricesConnectionChange,
    ],
  );

  const setNatsConnection = useCallback(
    (nats: NatsConnection | null) => {
      dispatch({ type: 'UPDATE_NATS_CONNECTION', payload: { nats } });
      onNatsConnectionChange?.(nats);
    },
    [onNatsConnectionChange],
  );

  // ============================================
  // Prices WebSocket Initialization
  // ============================================

  const initPricesSocket = useCallback(async () => {
    if (!config.pricesWebSocketUrl || !config.hashToken) {
      console.warn('[SocketManagerProvider] Prices WebSocket config missing');
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_CONFIG_MISSING, {
        enabled: config?.enablePrices,
        url: config?.pricesWebSocketUrl,
        hasHashToken: !!config?.hashToken,
        client: 'PRICES',
      });
      return;
    }

    try {
      const pricesConfig = {
        websocketUrl: config.pricesWebSocketUrl,
        socketId: WebSocketClientType.PRICES,
        websocketConfig: {
          reconnectInterval: config.reconnectInterval ?? 2000,
          maxReconnectAttempts: config.maxReconnectAttempts ?? 5,
          logger: config.logger,
          analytics: config.analytics,
        },
      };

      PricesWebSocketManager.initializePricesSocketManager(
        pricesConfig,
        setWebSocketConnection,
        config.hashToken,
      );

      const pricesWsManager = PricesWebSocketManager.getPricesWSInstance();

      if (!pricesWsManager) {
        throw new Error('PricesWebSocketManager not initialized');
      }

      if (pricesWsManager.getPricesConnection()) {
        return;
      }

      await pricesWsManager.initializePriceConnection();
    } catch (error: unknown) {
      console.error(
        '[SocketManagerProvider] Error initializing prices socket',
        error,
      );
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        client: 'PRICES',
      });
    }
  }, [config, setWebSocketConnection]);

  // ============================================
  // Trades WebSocket Initialization
  // ============================================

  const initTradesSocket = useCallback(async () => {
    if (!config.tradesWebSocketUrl || !config.hashToken) {
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_CONFIG_MISSING, {
        enabled: config?.enableTrades,
        url: config?.tradesWebSocketUrl,
        hasHashToken: !!config?.hashToken,
        client: 'TRADES',
      });
      return;
    }

    try {
      const tradesConfig = {
        websocketUrl: config.tradesWebSocketUrl,
        socketId: WebsocketClientType.TRADES,
        websocketConfig: {
          reconnectInterval: config.reconnectInterval ?? 2000,
          maxReconnectAttempts: config.maxReconnectAttempts ?? 5,
          logger: config.logger,
          analytics: config.analytics,
        },
      };

      TradesWebSocketManager.initializeTradesSocketManager(
        tradesConfig,
        setWebSocketConnection,
        config.hashToken,
      );

      const tradesWsManager = TradesWebSocketManager.getTradesWSInstance();

      if (!tradesWsManager) {
        throw new Error('TradesWebSocketManager not initialized');
      }

      if (tradesWsManager.getTradesConnection()) {
        return;
      }

      await tradesWsManager.initializeTradesConnection();
    } catch (error: unknown) {
      console.error(
        '[SocketManagerProvider] Error initializing trades socket',
        error,
      );
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        client: 'TRADES',
      });
    }
  }, [config, setWebSocketConnection]);

  // ============================================
  // US Prices WebSocket Initialization
  // ============================================

  const initUSPricesSocket = useCallback(async () => {
    if (!config.usPricesWebSocketUrl || !config.hashToken) {
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_CONFIG_MISSING, {
        enabled: config?.enableUSPrices,
        url: config?.usPricesWebSocketUrl,
        hasHashToken: !!config?.hashToken,
        client: 'US_PRICES',
      });
      return;
    }

    try {
      const usPricesConfig = {
        websocketUrl: config.usPricesWebSocketUrl,
        socketId: WebSocketClientType.US_PRICES,
        websocketConfig: {
          reconnectInterval: config.reconnectInterval ?? 2000,
          maxReconnectAttempts: config.maxReconnectAttempts ?? 5,
          logger: config.logger,
          analytics: config.analytics,
        },
      };

      USPricesWebSocketManager.initializeUSPricesSocketManager(
        usPricesConfig,
        setWebSocketConnection,
        config.hashToken,
      );

      const usPricesWsManager =
        USPricesWebSocketManager.getUSPricesWSInstance();

      if (!usPricesWsManager) {
        throw new Error('USPricesWebSocketManager not initialized');
      }

      if (usPricesWsManager.getUSPricesConnection()) {
        return;
      }

      await usPricesWsManager.initializeUSPriceConnection();
    } catch (error: unknown) {
      console.error(
        '[SocketManagerProvider] Error initializing US prices socket',
        error,
      );
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        client: 'US_PRICES',
      });
    }
  }, [config, setWebSocketConnection]);

  // ============================================
  // NATS Initialization
  // ============================================

  const initNatsConnection = useCallback(async () => {
    if (!config.natsEnabled || !config.natsServer || !config.natsToken) {
      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_CONFIG_MISSING, {
        enabled: !!config?.natsEnabled,
        url: config?.natsServer,
        hasToken: !!config?.natsToken,
        client: 'NATS',
      });
      return;
    }

    try {
      const natsManager = NatsManager.getInstance();

      await natsManager.connect({
        server: config.natsServer,
        token: config.natsToken,
        reconnect: true,
        maxReconnectAttempts: config.maxReconnectAttempts ?? -1,
        reconnectTimeWait: config.reconnectInterval ?? 2000,
        waitOnFirstConnect: true,
        logger: config.logger,
        analytics: config.analytics,
      });

      const connection = natsManager.getConnection();
      setNatsConnection(connection);

      // Update PricesWebSocketManager with NATS connection status
      if (connection) {
        PricesWebSocketManager.setNatsConnection(
          connection.isClosed() ? 'closed' : 'connected',
        );
      }
    } catch (error: unknown) {
      console.error('[SocketManagerProvider] Error initializing NATS', error);

      trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        client: 'NATS',
      });
    }
  }, [config, setNatsConnection]);

  // ============================================
  // Initialize All Connections
  // ============================================

  const initializeConnections = useCallback(async () => {
    const initPromises: Promise<void>[] = [];

    if (config.enablePrices && config.pricesWebSocketUrl) {
      initPromises.push(initPricesSocket());
    }

    if (config.enableTrades && config.tradesWebSocketUrl) {
      initPromises.push(initTradesSocket());
    }

    if (config.enableUSPrices && config.usPricesWebSocketUrl) {
      initPromises.push(initUSPricesSocket());
    }

    if (config.natsEnabled) {
      initPromises.push(initNatsConnection());
    }

    trackEvent(PROVIDER_EVENT_NAMES.INIT_CONNECTIONS_START, {
      enablePrices: config?.enablePrices,
      enableTrades: config?.enableTrades,
      enableUSPrices: config?.enableUSPrices,
      natsEnabled: config?.natsEnabled,
    });

    await Promise.all(initPromises);
  }, [
    config,
    initPricesSocket,
    initTradesSocket,
    initUSPricesSocket,
    initNatsConnection,
  ]);

  // ============================================
  // Cleanup Function
  // ============================================

  const cleanup = useCallback(() => {
    // Disconnect Prices WebSocket
    const pricesWsManager = PricesWebSocketManager.getPricesWSInstance();
    if (pricesWsManager) {
      void pricesWsManager.disconnectPriceConnection();
    }

    // Disconnect Trades WebSocket
    const tradesWsManager = TradesWebSocketManager.getTradesWSInstance();
    if (tradesWsManager) {
      void tradesWsManager.disconnectTradesConnection();
    }

    // Disconnect US Prices WebSocket
    const usPricesWsManager = USPricesWebSocketManager.getUSPricesWSInstance();
    if (usPricesWsManager) {
      void usPricesWsManager.disconnectUSPriceConnection();
    }

    // Disconnect NATS
    if (config.natsEnabled) {
      void NatsManager.getInstance().disconnect();
    }
  }, [config.natsEnabled]);

  // ============================================
  // Effect: Initialize on mount
  // ============================================

  useEffect(() => {
    if (enabled) {
      void initializeConnections();
    }

    return () => {
      cleanup();
    };
  }, [enabled, initializeConnections, cleanup]);

  // ============================================
  // Effect: Watch for NATS token changes and reconnect
  // ============================================

  const prevNatsTokenRef = useRef<string | undefined>(config.natsToken);

  useEffect(() => {
    const prevToken = prevNatsTokenRef.current;
    const newToken = config.natsToken;

    // Only reconnect if:
    // 1. Provider is enabled
    // 2. NATS is enabled
    // 3. Token has changed (and we had a previous token)
    // 4. New token is valid
    if (
      enabled &&
      config.natsEnabled &&
      prevToken &&
      newToken &&
      prevToken !== newToken
    ) {
      // Disconnect existing connection and reconnect with new token
      void NatsManager.getInstance()
        .disconnect()
        .then(() => {
          void initNatsConnection();
        });

      trackEvent(PROVIDER_EVENT_NAMES.NATS_TOKEN_CHANGED, {
        enabled: config?.natsEnabled,
        url: config?.natsServer,
        hasToken: !!config?.natsToken,
        client: 'NATS',
      });
    }

    // Update ref for next comparison
    prevNatsTokenRef.current = newToken;
  }, [enabled, config.natsEnabled, config.natsToken, initNatsConnection]);

  // ============================================
  // Context Value
  // ============================================

  const contextValue = useMemo<SocketManagerContextValue>(
    () => ({
      pricesConnection: state.pricesConnection,
      tradesConnection: state.tradesConnection,
      usPricesConnection: state.usPricesConnection,
      natsConnection: state.natsConnection,
      isPricesConnected: state.pricesConnection?.readyState === WebSocket.OPEN,
      isTradesConnected: state.tradesConnection?.readyState === WebSocket.OPEN,
      isUSPricesConnected:
        state.usPricesConnection?.readyState === WebSocket.OPEN,
      isNatsConnected:
        state.natsConnection !== null && !state.natsConnection.isClosed(),
      config,
    }),
    [state, config],
  );

  return (
    <SocketManagerContext.Provider value={contextValue}>
      {children}
    </SocketManagerContext.Provider>
  );
}

export default SocketManagerProvider;
